import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import {
  acceptQuotation,
  approveAodAsGceo,
  approveAodAsGcoo,
  getInvoices,
  getPendingAods,
  getQuotations,
  issueInvoice,
  offerQuotation,
  rejectAod,
} from "../../../services/revenue";
import { useToast } from "../../../hooks/useToast";
import {
  OperationsConfirmDialog,
  ifasDisabledBtnSx,
  ifasTextOrange,
  moneyCellSx,
  operationsClickableRowSx,
} from "../ifas/components/OperationsUI";
import InvoiceDetailDialog from "./InvoiceDetailDialog";
import { formatR } from "../../../utils/money";

interface InvoiceRow {
  invoiceNumber: string;
  caseNumber?: string;
  clientName?: string;
  status: string;
  dueDate?: string;
  totalAmount?: number;
  outstandingAmount?: number;
}
interface QuotationRow {
  quotationNumber: string;
  quotationStatus: string;
  caseNumber?: string;
  claimantFirstName?: string;
  claimantLastName?: string;
  createdOn?: string;
}

const fmtDate = (d?: string) => (d ? String(d).slice(0, 10) : "-");

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  DRAFT: { bg: "#F3F4F6", fg: "#4B5563" },
  OFFERED: { bg: "#EEF2FF", fg: "#3730A3" },
  ACCEPTED: { bg: "#E6F4EC", fg: "#1A7F4B" },
  REJECTED: { bg: "#FBE8E4", fg: "#B3341F" },
  ISSUED: { bg: "#F3F4F6", fg: "#374151" },
  PAYMENT_PENDING: { bg: "#FBF1DC", fg: "#9A6A00" },
  PARTIALLY_PAID: { bg: "#EEF2FF", fg: "#3730A3" },
  PAID: { bg: "#E6F4EC", fg: "#1A7F4B" },
  OVERDUE: { bg: "#FBE8E4", fg: "#B3341F" },
};

function StatusChip({ status }: { status: string }) {
  const c = STATUS_COLORS[status] || { bg: "#F3F4F6", fg: "#4B5563" };
  return <Chip label={String(status).replace(/_/g, " ")} size="small" sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 700, fontSize: 11 }} />;
}

function CountChips({ counts }: { counts: Record<string, number> }) {
  const entries = Object.entries(counts || {}).filter(([, v]) => v > 0);
  if (!entries.length) return null;
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
      {entries.map(([k, v]) => (
        <Chip key={k} label={`${k.replace(/_/g, " ")}: ${v}`} size="small"
          sx={{ bgcolor: (STATUS_COLORS[k] || { bg: "#F3F4F6" }).bg, color: (STATUS_COLORS[k] || { fg: "#4B5563" }).fg, fontWeight: 700 }} />
      ))}
    </Stack>
  );
}

export default function RevenuePortal() {
  const { showInfoToast } = useToast();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [invoiceCounts, setInvoiceCounts] = useState<Record<string, number>>({});
  const [quotations, setQuotations] = useState<QuotationRow[]>([]);
  const [quotationCounts, setQuotationCounts] = useState<Record<string, number>>({});
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [dialogInvoice, setDialogInvoice] = useState<string | null>(null);
  const [aods, setAods] = useState<any[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Key of the single in-flight action, so only the clicked button spins.
  const [pending, setPending] = useState<string | null>(null);
  // Consequential action awaiting explicit confirmation.
  const [confirmIssue, setConfirmIssue] = useState<QuotationRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const [invRes, quoRes, aodRes] = await Promise.all([
      getInvoices(0, 100),
      getQuotations(0, 100),
      getPendingAods(),
    ]);
    if (invRes?.status === 200 && invRes.data) {
      setInvoices(invRes.data.invoices ?? []);
      setInvoiceCounts(invRes.data.statusCounts ?? {});
    }
    if (quoRes?.status === 200 && quoRes.data) {
      setQuotations(quoRes.data.quotations ?? []);
      setQuotationCounts(quoRes.data.statusCounts ?? {});
    }
    if (aodRes?.status === 200) setAods(Array.isArray(aodRes.data) ? aodRes.data : []);

    // A failed load must never look like "no money outstanding" — that is a
    // finance figure a user will act on. Surface the failure instead.
    if (invRes?.status !== 200) {
      setInvoices([]);
      setInvoiceCounts({});
      setLoadError(
        "Could not load invoices. The figures below are unavailable — this is not a zero balance.",
      );
    }
    setLoading(false);
  }, []);

  // ---- AoD approval chain (SOP §12.1, FR-095: GCOO *and* GCEO) ----
  const runAodAction = async (key: string, fn: () => Promise<any>, okMsg: string) => {
    if (busy) return;
    setBusy(true);
    setPending(key);
    const res = await fn();
    setBusy(false);
    setPending(null);
    if (res?.status === 200 || res?.status === 201) {
      showInfoToast(okMsg);
      await load();
    } else if (res?.status === 403) {
      showInfoToast("You are not in the required GCOO/GCEO group for this approval.");
    } else {
      showInfoToast(res?.data?.message || "Action failed.");
    }
  };

  useEffect(() => { load(); }, [load]);

  const shownInvoices = useMemo(
    () => (outstandingOnly ? invoices.filter((i) => (i.outstandingAmount ?? 0) > 0) : invoices),
    [invoices, outstandingOnly],
  );
  const totalOutstanding = useMemo(() => invoices.reduce((s, i) => s + (i.outstandingAmount ?? 0), 0), [invoices]);

  // ---- quotation lifecycle actions ----
  const runQuoteAction = async (key: string, fn: () => Promise<any>, okMsg: string, onInvoice?: (n: string) => void) => {
    if (busy) return;
    setBusy(true);
    setPending(key);
    const res = await fn();
    setBusy(false);
    setPending(null);
    if (res?.status === 200 || res?.status === 201) {
      showInfoToast(okMsg);
      if (onInvoice && res.data?.invoiceNumber) onInvoice(res.data.invoiceNumber);
      await load();
    } else {
      showInfoToast(res?.data?.message || "Action failed.");
    }
  };

  const clientNameOf = (q: QuotationRow) =>
    [q.claimantFirstName, q.claimantLastName].filter(Boolean).join(" ") || "Client";

  const doIssueInvoice = (q: QuotationRow) =>
    runQuoteAction(
      `issue:${q.quotationNumber}`,
      () => issueInvoice(q.quotationNumber),
      "Invoice issued",
      (n) => setTimeout(() => { setTab(0); setDialogInvoice(n); }, 300),
    );

  const spinner = (key: string) => (pending === key ? <CircularProgress size={14} color="inherit" /> : undefined);

  const quoteAction = (q: QuotationRow) => {
    const client = clientNameOf(q);
    switch (q.quotationStatus) {
      case "DRAFT":
        return (
          <Button size="small" variant="outlined" disabled={busy} sx={ifasDisabledBtnSx}
            startIcon={spinner(`send:${q.quotationNumber}`)}
            onClick={() => runQuoteAction(`send:${q.quotationNumber}`, () => offerQuotation(q.quotationNumber), "Quotation sent to client")}>
            Send
          </Button>
        );
      case "OFFERED":
        return (
          <Button size="small" variant="contained" color="success" disabled={busy} sx={ifasDisabledBtnSx}
            startIcon={spinner(`accept:${q.quotationNumber}`)}
            onClick={() => runQuoteAction(`accept:${q.quotationNumber}`, () => acceptQuotation(q.quotationNumber, client), "Client acceptance recorded")}>
            Accept
          </Button>
        );
      case "ACCEPTED":
        return (
          <Button size="small" variant="contained" disabled={busy} sx={ifasDisabledBtnSx}
            startIcon={spinner(`issue:${q.quotationNumber}`)}
            onClick={() => setConfirmIssue(q)}>
            Issue invoice
          </Button>
        );
      default:
        return <Typography fontSize={12} color="#9AA5B1">—</Typography>;
    }
  };

  return (
    <Box sx={{ flexGrow: 1, overflow: "auto" }}>
      <Box p={3}>
        <Typography fontSize={22} fontWeight={800} color="#111827" mb={0.5}>Revenue</Typography>
        <Typography fontSize={13} color="#6B7280" mb={2}>Quotations, tax invoices and outstanding payments</Typography>

        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #E5E7EB", overflow: "hidden" }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: "1px solid #E5E7EB", px: 2 }}>
            <Tab label={`Invoices${invoices.length ? ` (${invoices.length})` : ""}`} sx={{ fontWeight: 700 }} />
            <Tab label={`Quotations${quotations.length ? ` (${quotations.length})` : ""}`} sx={{ fontWeight: 700 }} />
            <Tab label={`AoD approvals${aods.length ? ` (${aods.length})` : ""}`} sx={{ fontWeight: 700 }} />
          </Tabs>

          <Box p={2.5}>
            {loading ? (
              <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress size={22} /></Box>
            ) : tab === 0 ? (
              <>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" useFlexGap>
                  <CountChips counts={invoiceCounts} />
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography fontSize={13} fontWeight={700} color="#374151">Total outstanding:&nbsp;
                    {loadError
                      ? <span style={{ color: "#6B7280" }}>unavailable</span>
                      : <span style={{ color: "#B3341F" }}>{formatR(totalOutstanding)}</span>}
                  </Typography>
                    <Chip clickable label={outstandingOnly ? "Showing outstanding" : "Outstanding only"} onClick={() => setOutstandingOnly((v) => !v)} size="small"
                      sx={{ bgcolor: outstandingOnly ? "#FBE8E4" : "#F3F4F6", color: outstandingOnly ? "#B3341F" : "#4B5563", fontWeight: 700 }} />
                  </Stack>
                </Stack>
                <TableContainer sx={{ border: "1px solid #E5E7EB", borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                      {/* Status and the money sit directly after the invoice number so
                          they are readable without scrolling the table sideways. */}
                      <TableRow>
                        {["Invoice #", "Status", "Outstanding", "Total", "Client", "Case", "Due Date"].map((h) => (
                          <TableCell
                            key={h}
                            align={h === "Outstanding" || h === "Total" ? "right" : "left"}
                            sx={{ fontWeight: 800, color: "#374151", whiteSpace: "nowrap" }}
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shownInvoices.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                            {loadError ? (
                              <Stack spacing={1} alignItems="center">
                                <Typography fontSize={13} fontWeight={700} color="#B3341F">
                                  Invoices could not be loaded
                                </Typography>
                                <Typography fontSize={12.5} color="#6B7280">{loadError}</Typography>
                                <Button size="small" variant="outlined" onClick={load}>Retry</Button>
                              </Stack>
                            ) : (
                              <span style={{ color: "#6B7280" }}>No invoices</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ) : shownInvoices.map((inv) => (
                        <TableRow
                          key={inv.invoiceNumber}
                          hover
                          tabIndex={0}
                          aria-label={`Open invoice ${inv.invoiceNumber}`}
                          sx={operationsClickableRowSx}
                          onClick={() => setDialogInvoice(inv.invoiceNumber)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setDialogInvoice(inv.invoiceNumber);
                            }
                          }}
                        >
                          <TableCell sx={{ fontWeight: 700, color: ifasTextOrange }}>{inv.invoiceNumber}</TableCell>
                          <TableCell><StatusChip status={inv.status} /></TableCell>
                          <TableCell sx={{ ...moneyCellSx, fontWeight: 700, color: (inv.outstandingAmount ?? 0) > 0 ? "#B3341F" : "#1A7F4B" }}>{formatR(inv.outstandingAmount)}</TableCell>
                          <TableCell sx={moneyCellSx}>{formatR(inv.totalAmount)}</TableCell>
                          <TableCell>{inv.clientName || "-"}</TableCell>
                          <TableCell>{inv.caseNumber || "-"}</TableCell>
                          <TableCell>{fmtDate(inv.dueDate)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography fontSize={12} color="#9AA5B1" mt={1}>Tip: click an invoice to record / confirm payment or download the tax invoice.</Typography>
              </>
            ) : tab === 2 ? (
              <>
                <Typography fontSize={13} color="#6B7280" mb={1.5}>
                  An Acknowledgement of Debt releases a case from the payment hold. Per SOP §12.1
                  it needs <b>both</b> the GCOO and GCEO offices — one approval alone is not enough.
                </Typography>
                <TableContainer sx={{ border: "1px solid #E5E7EB", borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                      <TableRow>
                        {["AoD #", "Case", "Amount", "Reason", "Status", "GCOO", "GCEO", "Actions"].map((h) => (
                          <TableCell key={h} align={h === "Amount" ? "right" : "left"} sx={{ fontWeight: 800, color: "#374151", whiteSpace: "nowrap" }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {aods.length === 0 ? (
                        <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: "#6B7280" }}>No AoD requests awaiting approval</TableCell></TableRow>
                      ) : aods.map((a: any) => (
                        <TableRow key={a.aodNumber} hover>
                          <TableCell sx={{ fontWeight: 700, color: ifasTextOrange }}>{a.aodNumber}</TableCell>
                          <TableCell>{a.caseNumber}</TableCell>
                          <TableCell sx={moneyCellSx}>{formatR(a.amount)}</TableCell>
                          <TableCell sx={{ maxWidth: 260 }}>
                            <Typography fontSize={13} noWrap title={a.reason}>{a.reason}</Typography>
                          </TableCell>
                          <TableCell><StatusChip status={a.status} /></TableCell>
                          <TableCell>{a.gcooApprovedBy || "—"}</TableCell>
                          <TableCell>{a.gceoApprovedBy || "—"}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              <Button size="small" variant="outlined" disabled={busy || Boolean(a.gcooApprovedAt)} sx={ifasDisabledBtnSx}
                                startIcon={spinner(`gcoo:${a.aodNumber}`)}
                                onClick={() => runAodAction(`gcoo:${a.aodNumber}`, () => approveAodAsGcoo(a.aodNumber), `GCOO approved ${a.aodNumber}`)}>
                                GCOO
                              </Button>
                              <Button size="small" variant="outlined" disabled={busy || Boolean(a.gceoApprovedAt)} sx={ifasDisabledBtnSx}
                                startIcon={spinner(`gceo:${a.aodNumber}`)}
                                onClick={() => runAodAction(`gceo:${a.aodNumber}`, () => approveAodAsGceo(a.aodNumber), `GCEO approved ${a.aodNumber}`)}>
                                GCEO
                              </Button>
                              <Button size="small" variant="text" color="error" disabled={busy} sx={{ ...ifasDisabledBtnSx, color: "#B3341F", fontWeight: 700 }}
                                startIcon={spinner(`reject:${a.aodNumber}`)}
                                onClick={() => {
                                  const reason = window.prompt(`Reason for rejecting ${a.aodNumber}?`);
                                  if (reason) runAodAction(`reject:${a.aodNumber}`, () => rejectAod(a.aodNumber, reason), `${a.aodNumber} rejected`);
                                }}>
                                Reject
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : (
              <>
                <CountChips counts={quotationCounts} />
                <TableContainer sx={{ border: "1px solid #E5E7EB", borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                      <TableRow>
                        {["Quotation #", "Status", "Client", "Case", "Created", "Actions"].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 800, color: "#374151", whiteSpace: "nowrap" }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {quotations.length === 0 ? (
                        <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: "#6B7280" }}>No quotations</TableCell></TableRow>
                      ) : quotations.map((q) => (
                        <TableRow key={q.quotationNumber} hover>
                          <TableCell sx={{ fontWeight: 700, color: "#111827" }}>{q.quotationNumber}</TableCell>
                          <TableCell><StatusChip status={q.quotationStatus} /></TableCell>
                          <TableCell>{[q.claimantFirstName, q.claimantLastName].filter(Boolean).join(" ") || "-"}</TableCell>
                          <TableCell>{q.caseNumber || "-"}</TableCell>
                          <TableCell>{fmtDate(q.createdOn)}</TableCell>
                          <TableCell>{quoteAction(q)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        </Paper>
      </Box>

      <OperationsConfirmDialog
        open={Boolean(confirmIssue)}
        title="Issue tax invoice?"
        busy={busy}
        confirmLabel="Issue invoice"
        message={
          confirmIssue ? (
            <>
              This converts quotation <b>{confirmIssue.quotationNumber}</b> for{" "}
              <b>{clientNameOf(confirmIssue)}</b> into a tax invoice. Once issued the
              quotation can no longer be changed.
            </>
          ) : null
        }
        onCancel={() => setConfirmIssue(null)}
        onConfirm={() => {
          const q = confirmIssue;
          setConfirmIssue(null);
          if (q) doIssueInvoice(q);
        }}
      />

      <InvoiceDetailDialog
        invoiceNumber={dialogInvoice}
        open={Boolean(dialogInvoice)}
        onClose={() => setDialogInvoice(null)}
        onChanged={load}
      />
    </Box>
  );
}
